import UDActionButton from 'ud-ui/components/action-button';
import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Clock, Copy, FolderOpen, Pencil, Trash2 } from 'lucide-react';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import React from 'react';
import { theme } from 'styles/theme/theme';
import { UDButton, UDText } from 'ud-ui/components';
import EmptyButton from 'ud-ui/components/empty-button';
import UDCard from 'ud-ui/components/card';
import UDTippy from 'ud-ui/components/tippy';
import { GROUP_CARD_LAYOUT_CLASS, GROUP_CARD_PROPS, GROUP_COVER_CLASS, PRESET_CARD_ACTIONS_GAP } from './preset-card-metrics';

interface IProps {
  group: MediaManagerTemplateGroup;
  canWrite?: boolean;
  canDuplicate?: boolean;
  isDuplicating?: boolean;
  onOpen: (group: MediaManagerTemplateGroup) => void;
  onRename?: (group: MediaManagerTemplateGroup) => void;
  onDelete?: (group: MediaManagerTemplateGroup) => void;
  onDuplicate?: (group: MediaManagerTemplateGroup) => void;
}

function formatUpdatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('ru-RU');
}

function MediaManagerTemplateGroupCard(props: IProps) {
  const { group, canWrite, canDuplicate, isDuplicating, onOpen, onRename, onDelete, onDuplicate } = props;
  const { t } = useLingui();

  return (
    <UDCard {...GROUP_CARD_PROPS} className={GROUP_CARD_LAYOUT_CLASS}>
      {/* Обложка и заголовок — одна кнопка на всю верхнюю часть карточки: попасть по ней проще, чем по «Открыть».
          Действия внизу в неё не вложены, поэтому интерактивные элементы друг в друга не входят. */}
      <UDActionButton as="button" className="flex min-w-0 cursor-pointer flex-col text-left" onClick={() => onOpen(group)}>
        <div className={GROUP_COVER_CLASS}>
          <div
            className="rounded-panel flex size-16 items-center justify-center"
            style={{ background: `color-mix(in srgb, ${theme.colors.DarkYellow} 14%, transparent)` }}
          >
            <FolderOpen size={32} color={theme.colors.DarkYellow} aria-hidden />
          </div>
          <div className="bg-surfaceCard rounded-pill absolute top-3 right-3 px-3 py-1">
            <UDText variant="text-2" as="span" className="text-inkSecondary">
              {plural(group.templatesCount, { one: '# шаблон', few: '# шаблона', many: '# шаблонов', other: '# шаблонов' })}
            </UDText>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-2 px-4 pt-4 pb-3">
          <UDText variant="title-4-bold" as="div" className="text-inkPrimary truncate">
            {group.name}
          </UDText>
          {group.updatedAt && (
            <div className="text-inkMuted flex items-center gap-1.5">
              <Clock size={14} aria-hidden />
              <UDText variant="text-2" as="span" color={theme.colors.InkMuted}>
                {t`изменён ${formatUpdatedAt(group.updatedAt)}`}
              </UDText>
            </div>
          )}
        </div>
      </UDActionButton>

      <div className="border-lineSubtle mt-auto flex items-center justify-between border-t px-4 py-3">
        {/* 16px отступа кита + 3px с каждой стороны: кнопка шире на 6px при любой подписи. */}
        <UDButton variant="primary" style={{ paddingInline: 19 }} onClick={() => onOpen(group)}>
          <Trans>Открыть</Trans>
        </UDButton>
        <div className="flex items-center" style={{ gap: PRESET_CARD_ACTIONS_GAP }}>
          {canDuplicate && onDuplicate && (
            <UDTippy content={t`Дублировать группу в организацию`} placement={'top'}>
              <EmptyButton
                icon={<Copy size={20} />}
                disabled={isDuplicating}
                loading={isDuplicating}
                loaderSize={20}
                aria-label={t`Дублировать группу в организацию`}
                onClick={() => onDuplicate(group)}
              />
            </UDTippy>
          )}
          {canWrite && onRename && (
            <UDTippy content={t`Переименовать`} placement={'top'}>
              <EmptyButton icon={<Pencil size={20} />} aria-label={t`Переименовать`} onClick={() => onRename(group)} />
            </UDTippy>
          )}
          {canWrite && onDelete && (
            <UDTippy content={t`Удалить`} placement={'top'}>
              <EmptyButton
                color={theme.colors.error}
                icon={<Trash2 size={20} />}
                aria-label={t`Удалить`}
                onClick={() => onDelete(group)}
              />
            </UDTippy>
          )}
        </div>
      </div>
    </UDCard>
  );
}

export default MediaManagerTemplateGroupCard;
